import { cx } from '@flowus/common/cx';
import { PermissionRole } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { Icon } from 'src/common/components/icon';
import { ListView } from 'src/common/components/list-view';
import type { ListItem } from 'src/common/components/list-view/types';
import { ListItemType } from 'src/common/components/list-view/types';
import { useOpenModal } from 'src/common/components/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export const permissionName = (t: (key: string) => string) => ({
  [PermissionRole.EDITOR]: t('common.full_access'),
  [PermissionRole.WRITER]: t('common.can_edit'),
  [PermissionRole.COMMENTER]: t('common.can_comment'),
  [PermissionRole.READER]: t('common.can_view'),
  [PermissionRole.NONE]: t('common.no_access_permission'),
});

export interface Props {
  showRight?: boolean;
  role?: PermissionRole;
  isSpaceType?: boolean;
  noRemove?: boolean;
  onChange?: (role: PermissionRole) => void;
  isGuest?: boolean;
  isForm?: boolean;
}

export const Permissions: FC<Props> = ({
  isGuest,
  showRight,
  role,
  onChange,
  isSpaceType,
  noRemove,
  isForm,
}) => {
  const openModal = useOpenModal();
  const { t } = useAppTranslation();

  const permissionName = {
    [PermissionRole.EDITOR]: t('common.full_access'),
    [PermissionRole.WRITER]: t('common.can_edit'),
    [PermissionRole.COMMENTER]: t('common.can_comment'),
    [PermissionRole.READER]: t('common.can_view'),
    [PermissionRole.NONE]: t('common.no_access_permission'),
  };

  let items: ListItem[] = [];

  if (!isGuest) {
    items.push({
      type: ListItemType.TEXT_DESCRIPTION,
      data: {
        title: t('common.full_access'),
        description: t('share.permissions.allPermissionsDesc'),
        selected: role === PermissionRole.EDITOR,
        onClick: () => onChange?.(PermissionRole.EDITOR),
      },
    });
  }

  items.push({
    type: ListItemType.TEXT_DESCRIPTION,
    data: {
      title: t('common.can_edit'),
      description: t('share.permissions.canEditDesc'),
      selected: role === PermissionRole.WRITER,
      onClick: () => onChange?.(PermissionRole.WRITER),
    },
  });

  items.push({
    type: ListItemType.TEXT_DESCRIPTION,
    data: {
      title: t('common.can_comment'),
      description: t('share.permissions.canCommentDesc'),
      selected: role === PermissionRole.COMMENTER,
      onClick: () => onChange?.(PermissionRole.COMMENTER),
    },
  });

  items.push({
    type: ListItemType.TEXT_DESCRIPTION,
    data: {
      title: t('common.can_view'),
      description: t('share.permissions.canViewDesc'),
      selected: role === PermissionRole.READER,
      onClick: () => onChange?.(PermissionRole.READER),
    },
  });

  if (!noRemove) {
    items.push({ type: ListItemType.LINE });
    items.push({
      type: ListItemType.TEXT_DESCRIPTION,
      data: {
        title: (
          <div className="text-red">
            {isSpaceType ? t('common.no_access_permission') : t('common.remove')}
          </div>
        ),
        selected: isSpaceType && (!role || role === PermissionRole.NONE),
        onClick: () => onChange?.(PermissionRole.NONE),
      },
    });
  }
  if (isForm) {
    items = [
      {
        type: ListItemType.TEXT_DESCRIPTION,
        data: {
          title: <div className="text-red">{t('common.remove')}</div>,
          onClick: () => onChange?.(PermissionRole.NONE),
        },
      },
    ];
  }

  /** 用户权限修改弹窗 */
  const openOption = (event: MouseEvent<HTMLDivElement>) => {
    openModal.dropdown({
      placement: 'bottom',
      offset: [0, 4],
      popcorn: event.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="py-2 w-[270px] next-modal"
          onItemClick={() => onCloseModal()}
          items={items}
        />
      ),
    });
  };

  return (
    <div
      hidden={!onChange && !showRight}
      className={cx(
        'self-center shrink-0 flex items-center text-t4 text-grey3',
        onChange ? 'cursor-pointer' : 'cursor-not-allowed',
        onChange && 'animate-click'
      )}
      onClick={(e) => onChange && openOption(e)}
    >
      {isForm ? (
        t('common.can_fill')
      ) : (
        <>{role ? permissionName[role] : permissionName[PermissionRole.NONE]}</>
      )}
      {onChange && <Icon name="IcArrowDown01" className="ml-1" size="xxxsmall" />}
    </div>
  );
};
